import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import {
    Plus,
    Edit,
    Trash2,
    Loader2,
    X,
    Check,
    Power,
    Save
} from 'lucide-react'
import toast from 'react-hot-toast'
import ConfirmModal from '../../components/ConfirmModal'
import './QualificationsPage.css'

export default function QualificationsPage() {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState('QUALITE') // QUALITE or CONSEILLER
    const [qualifications, setQualifications] = useState([])
    const [loading, setLoading] = useState(true)

    const [showModal, setShowModal] = useState(false)
    const [editingItem, setEditingItem] = useState(null)
    const [modalLoading, setModalLoading] = useState(false)
    const [formData, setFormData] = useState({
        nomQualification: '',
        actionType: 'NONE' // NONE, TRANSFERT, SIGNEE, ANNULE, ERREUR
    })

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        item: null,
        action: null, // 'delete' or 'toggle'
        loading: false
    })

    useEffect(() => {
        fetchQualifications()
    }, [activeTab])

    const fetchQualifications = async () => {
        setLoading(true)
        try {
            const response = await api.get('/qualifications', { params: { type: activeTab } })
            setQualifications(response.data.data.qualifications)
        } catch (error) {
            toast.error(t('common.error'))
        } finally {
            setLoading(false)
        }
    }

    const openAddModal = () => {
        setEditingItem(null)
        setFormData({
            nomQualification: '',
            actionType: 'NONE'
        })
        setShowModal(true)
    }

    const openEditModal = (item) => {
        setEditingItem(item)
        let type = 'NONE'
        if (item.isTransfert) type = 'TRANSFERT'
        else if (item.isSignee) type = 'SIGNEE'
        else if (item.isAnnule) type = 'ANNULE'
        else if (item.isErreur) type = 'ERREUR'

        setFormData({
            nomQualification: item.nomQualification,
            actionType: type
        })
        setShowModal(true)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setModalLoading(true)

        try {
            const payload = {
                ...formData,
                isTransfert: formData.actionType === 'TRANSFERT',
                isSignee: formData.actionType === 'SIGNEE',
                isAnnule: formData.actionType === 'ANNULE',
                isErreur: formData.actionType === 'ERREUR'
            }

            // Clean payload for display purposes (not strictly needed but good for debugging)
            delete payload.actionType

            if (editingItem) {
                await api.put(`/qualifications/${editingItem._id}`, payload)
            } else {
                await api.post('/qualifications', {
                    ...payload,
                    typeQualification: activeTab
                })
            }
            toast.success(t('common.success'))
            setShowModal(false)
            fetchQualifications()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
        } finally {
            setModalLoading(false)
        }
    }

    const handleDelete = (item) => {
        setConfirmModal({ open: true, item, action: 'delete', loading: false })
    }

    const handleToggleStatus = (item) => {
        setConfirmModal({ open: true, item, action: 'toggle', loading: false })
    }

    const confirmAction = async () => {
        const { item, action } = confirmModal
        if (!item) return

        setConfirmModal(prev => ({ ...prev, loading: true }))

        try {
            if (action === 'delete') {
                await api.delete(`/qualifications/${item._id}`)
            } else if (action === 'toggle') {
                const newStatus = item.etatQualification === 'ENABLED' ? 'DISABLED' : 'ENABLED'
                await api.put(`/qualifications/${item._id}`, { etatQualification: newStatus })
            }
            toast.success(t('common.success'))
            setConfirmModal({ open: false, item: null, action: null, loading: false })
            fetchQualifications()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
            setConfirmModal(prev => ({ ...prev, loading: false }))
        }
    }

    return (
        <div className="qualifications-page">
            <div className="page-header">
                <h1 className="page-title">Qualifications</h1>
                <button className="btn btn-primary" onClick={openAddModal}>
                    <Plus size={18} />
                    Ajouter
                </button>
            </div>

            <div className="tabs">
                <button
                    className={`tab ${activeTab === 'QUALITE' ? 'active' : ''}`}
                    onClick={() => setActiveTab('QUALITE')}
                >
                    Qualité
                </button>
                <button
                    className={`tab ${activeTab === 'CONSEILLER' ? 'active' : ''}`}
                    onClick={() => setActiveTab('CONSEILLER')}
                >
                    Conseiller
                </button>
            </div>

            <div className="card mt-4">
                {loading ? (
                    <div className="p-5 text-center"><Loader2 className="spin" /></div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Type</th>
                                <th>Action</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {qualifications.length === 0 ? (
                                <tr><td colSpan={5} className="text-center p-4">Aucune qualification</td></tr>
                            ) : (
                                qualifications.map(item => (
                                    <tr key={item._id} className={item.etatQualification === 'DISABLED' ? 'opacity-50' : ''}>
                                        <td>{item.nomQualification}</td>
                                        <td><span className="badge badge-neutral">{item.typeQualification}</span></td>
                                        <td>
                                            {item.isTransfert && <span className="badge badge-info">Transfert (Date)</span>}
                                            {item.isSignee && <span className="badge badge-success">Signature</span>}
                                            {item.isAnnule && <span className="badge badge-error">Annulé</span>}
                                            {item.isErreur && <span className="badge badge-warning">Erreur</span>}
                                            {!item.isTransfert && !item.isSignee && !item.isAnnule && !item.isErreur && <span className="text-muted">-</span>}
                                        </td>
                                        <td>
                                            <span className={`badge ${item.etatQualification === 'ENABLED' ? 'badge-success' : 'badge-error'}`}>
                                                {item.etatQualification === 'ENABLED' ? 'Actif' : 'Inactif'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button className="btn btn-icon btn-ghost" onClick={() => openEditModal(item)}>
                                                    <Edit size={16} />
                                                </button>
                                                <button className="btn btn-icon btn-ghost" onClick={() => handleToggleStatus(item)} title={item.etatQualification === 'ENABLED' ? 'Désactiver' : 'Activer'}>
                                                    <Power size={16} />
                                                </button>
                                                <button className="btn btn-icon btn-ghost text-danger" onClick={() => handleDelete(item)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingItem ? 'Modifier' : 'Ajouter'} Qualification</h3>
                            <button className="btn btn-icon btn-ghost" onClick={() => setShowModal(false)}><X size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">Nom *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.nomQualification}
                                        onChange={(e) => setFormData({ ...formData, nomQualification: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Action</label>
                                    <select
                                        className="form-select"
                                        value={formData.actionType}
                                        onChange={(e) => setFormData({ ...formData, actionType: e.target.value })}
                                    >
                                        <option value="NONE">Aucune</option>
                                        <option value="TRANSFERT">Transfert (Nécessite une date)</option>
                                        <option value="SIGNEE">Signature</option>
                                        <option value="ANNULE">Annulé</option>
                                        <option value="ERREUR">Erreur</option>
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                                <button type="submit" className="btn btn-primary" disabled={modalLoading}>
                                    {modalLoading ? <Loader2 size={18} className="spin" /> : 'Enregistrer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={confirmModal.open}
                onClose={() => setConfirmModal({ open: false, item: null, action: null })}
                onConfirm={confirmAction}
                title={confirmModal.action === 'delete' ? 'Supprimer' : 'Changer statut'}
                message={`Êtes-vous sûr de vouloir ${confirmModal.action === 'delete' ? 'supprimer' : 'changer le statut de'} "${confirmModal.item?.nomQualification}" ?`}
                type={confirmModal.action === 'delete' ? 'danger' : 'warning'}
                loading={confirmModal.loading}
            />
        </div>
    )
}
